// AddBirthPlaceCard — shown on the dashboard instead of a chart error when the
// account's birth place has no coordinates (needs_birthplace, or a place saved
// without lat/lon before the backend enforced it). Opens an inline place
// picker and saves through PUT /api/user-profile/{user_id}, which geocodes and
// stores coordinates + timezone (or answers 422 place_unresolved).
import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { searchPlaces, getBackendUrl } from '../services/apiClient';
import useBirthPlaceField from '../hooks/useBirthPlaceField';
import BirthPlaceHint from './BirthPlaceHint';
import { isPlaceUnresolved, isPlaceCheckBusy } from '../utils/birthPlace';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';

export default function AddBirthPlaceCard({ userInfo, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const searchTimer = useRef(null);
  const birthPlace = useBirthPlaceField(userInfo?.place || '', null, null, 'dashboard');
  const auth = useAuth();

  useEffect(() => { trackEvent('birthplace_cta_shown', { surface: 'dashboard' }); }, []);

  const openPicker = () => {
    setOpen(true);
    trackEvent('birthplace_cta_opened', { surface: 'dashboard' });
  };

  const onChange = (e) => {
    const value = e.target.value;
    birthPlace.onType(value);
    setSaveError('');
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      const data = await searchPlaces(value);
      setSuggestions(data.suggestions || []);
    }, 300);
  };

  const pick = (s) => {
    birthPlace.onPick(s);
    setSuggestions([]);
  };

  const save = async () => {
    if (!birthPlace.ready || !userInfo?.user_id) return;
    setSaving(true);
    setSaveError('');
    try {
      const token = localStorage.getItem('jyotish_session_token');
      const res = await fetch(`${getBackendUrl()}/api/user-profile/${encodeURIComponent(userInfo.user_id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          place: birthPlace.place,
          latitude: birthPlace.latitude,
          longitude: birthPlace.longitude,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (isPlaceCheckBusy(body)) birthPlace.markCheckFailed(); // "tap to retry"
        else if (isPlaceUnresolved(body)) birthPlace.markUnresolved();
        else setSaveError('Could not save your birth place. Please try again.');
        trackEvent('birthplace_cta_saved', { surface: 'dashboard', success: false, status: res.status });
        return;
      }
      trackEvent('birthplace_cta_saved', { surface: 'dashboard', success: true, source: birthPlace.status });
      const saved = {
        place: birthPlace.place,
        latitude: birthPlace.latitude,
        longitude: birthPlace.longitude,
        needs_birthplace: false,
      };
      // AuthContext's user is what UserContext re-derives userInfo from; if it
      // kept the old null coordinates, UserContext would put them straight back
      // (and the card would reappear). Update it first, then the chart state.
      if (auth?.setUser && auth.user) auth.setUser({ ...auth.user, ...saved });
      onSaved?.(saved);
    } catch {
      setSaveError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="add-birthplace-card"
      style={{
        border: '1px solid rgba(251,191,36,0.4)', borderRadius: 16, padding: 16,
        background: 'rgba(251,191,36,0.06)', margin: '12px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MapPin className="w-5 h-5 text-amber-400" />
        <p className="text-white font-medium">Add your birth place</p>
      </div>
      <p className="text-white/70 text-sm" style={{ marginTop: 6 }}>
        We couldn&apos;t find {userInfo?.place ? <>&ldquo;{userInfo.place}&rdquo;</> : 'your birth place'} on the map,
        so your chart can&apos;t be drawn yet. Choose it from the list to see your kundali.
      </p>

      {!open ? (
        <button type="button" onClick={openPicker} className="jp-btn-primary text-sm py-2"
          style={{ marginTop: 12 }} data-testid="add-birthplace-open">
          Add your birth place
        </button>
      ) : (
        <div style={{ marginTop: 12, position: 'relative' }}>
          <input
            type="text" value={birthPlace.place} onChange={onChange} autoFocus autoComplete="off"
            onBlur={() => setTimeout(() => birthPlace.resolveTyped(), 200)}
            placeholder="City, State, Country" data-testid="add-birthplace-input"
            className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-amber-400"
          />
          {suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-[#1e1e32] border border-white/20 rounded-xl shadow-xl max-h-60 overflow-y-auto">
              {suggestions.slice(0, 6).map((s, i) => (
                <button key={i} type="button" onClick={() => pick(s)}
                  className="w-full px-4 py-3 text-left hover:bg-amber-500/20 border-b border-white/10 last:border-b-0 text-white text-sm">
                  {s.label || s.name || s.full_name}
                </button>
              ))}
            </div>
          )}
          <BirthPlaceHint status={birthPlace.status} place={birthPlace.place} matchedName={birthPlace.matchedName} onRetry={birthPlace.resolveTyped} />
          {saveError && <p style={{ color: '#fca5a5', fontSize: 13, marginTop: 6 }}>{saveError}</p>}
          <button type="button" onClick={save} disabled={!birthPlace.ready || saving}
            className="jp-btn-primary text-sm py-2" style={{ marginTop: 12, opacity: (!birthPlace.ready || saving) ? 0.5 : 1 }}
            data-testid="add-birthplace-save">
            {saving ? <><Loader2 className="w-4 h-4 inline mr-2 animate-spin" />Saving…</> : 'Save birth place'}
          </button>
        </div>
      )}
    </div>
  );
}
